<template>
    <ui-sidepage extra-large :close="close" :title='`Cost Plan Breakdown`' :go-transparent="true"
                 :key="renderKey">
        <div>
            <div class="grid grid-cols-12 gap-2 mb-4">
                <div class="col-span-4">
                    <ui-toggle class="p-2 rounded-xl bg-gray-100" field="text-lg text-gray-900"
                               text="Show Materials Required"
                               v-model="breakdown.materials"></ui-toggle>
                </div>
                <div class="col-span-4">
                    <ui-toggle class="p-2 rounded-xl bg-gray-100" field="text-lg text-gray-900"
                               text="Show Budgets"
                               v-model="breakdown.budgets"></ui-toggle>
                </div>
            </div>
        </div>
        <div class=" rounded-lg border-gray-200 border px-4 py-2 ">
            <template v-if="breakdown.materials">
                <div class="w-full m-0 p-0 flex">
                    <div class="flex-1 flex-grow"><h1>Materials Required</h1></div>
                    <ui-toggle class="inline-block w-auto p-0.5 rounded-xl mr-8" field="text-xs"
                               v-model="split.sections"
                               :text="`Split Sections`"
                               :field="'text-gray-900'"
                    ></ui-toggle>
                </div>
                <template v-if="split.sections">
                    <div v-if="costPlan.sections.filter((i) => !i.omitted).length">
                        <div v-for="(section,sindex) in costPlan.sections.filter((i) => !i.omitted)"
                             class="px-4 py-2 my-2 border border-gray-200 rounded-lg bg-white hover:bg-blue-50">
                            <div class="w-full m-0 p-0 flex">
                                <div class="flex-1 flex-grow"><h1>Section - {{ section.reference }} -
                                    {{ section.name }}</h1></div>
                                <ui-toggle class="inline-block w-auto p-0.5 rounded-xl mr-4" field="text-xs"
                                           v-model="split.tasks"
                                           :text="`Split Tasks`"
                                           :field="'text-gray-900'"
                                ></ui-toggle>
                            </div>
                            <template v-if="split.tasks">
                                <div v-if="section.tasks.filter((i) => !i.omitted).length">
                                    <div v-for="(task,tindex) in section.tasks.filter((i) => !i.omitted)"
                                         class="px-4 py-2 my-2 border border-gray-200 rounded-lg bg-white hover:bg-blue-50">
                                        <div class="w-full m-0 p-0 flex">
                                            <div class="flex-1 flex-grow"><h1>Task - {{ task.reference }} - {{
                                                    task.name
                                                }}</h1>
                                            </div>
                                            <ui-toggle class="inline-block w-auto p-0.5 rounded-xl mr-0" field="text-xs"
                                                       v-model="split.items"
                                                       :text="`Split Items`"
                                                       :field="'text-gray-900'"
                                            ></ui-toggle>
                                        </div>
                                        <template v-if="split.items">
                                            <div v-if="task.items.filter((i) => !i.omitted).length">
                                                <div v-for="(item,iindex) in task.items.filter((i) => !i.omitted)"
                                                     class="px-4 py-2 my-2 border border-gray-200 rounded-lg bg-white hover:bg-blue-50">
                                                    <div class="w-full m-0 p-0 flex">
                                                        <div class="flex-1 flex-grow"><h1>Item - {{ item.reference }} -
                                                            {{ item.description }}</h1></div>
                                                    </div>
                                                    <cost-plan-breakdown-materials class="my-2"
                                                                                   :cost-plan="costPlan"
                                                                                   :materials="itemMaterials[item.id]"
                                                                                   :readonly="readonly"
                                                                                   @editMaterial="(from,to) => editMaterial(from,to,'item',item.id)"></cost-plan-breakdown-materials>

                                                </div>
                                            </div>
                                            <div v-else
                                                 class="flex border bg-gray-50 border-gray-200 mt-1 rounded rounded-lg -mb-px border-gray-200">
                                                <div
                                                    class="flex-1 -ml-px  text-center text-xs text-gray-400 p-2">
                                                    Task has no Items
                                                </div>
                                            </div>
                                        </template>
                                        <cost-plan-breakdown-materials class="my-2" :materials="taskMaterials[task.id]"
                                                                       :cost-plan="costPlan"
                                                                       :readonly="readonly" v-else
                                                                       @editMaterial="(from,to) => editMaterial(from,to,'task',task.id)"></cost-plan-breakdown-materials>
                                    </div>
                                </div>
                                <div v-else
                                     class="flex border bg-gray-50 border-gray-200 mt-1 rounded rounded-lg -mb-px border-gray-200">
                                    <div class="flex-1 -ml-px  text-center text-xs text-gray-400 p-2">
                                        Section has no Tasks
                                    </div>
                                </div>
                            </template>
                            <cost-plan-breakdown-materials class="my-2" :materials="sectionMaterials[section.id]"
                                                           :cost-plan="costPlan"
                                                           :readonly="readonly" v-else
                                                           @editMaterial="(from,to) => editMaterial(from,to,'section',section.id)"></cost-plan-breakdown-materials>
                        </div>
                    </div>
                    <div v-else
                         class="flex border bg-gray-50 border-gray-200 mt-1 rounded rounded-lg -mb-px border-gray-200">
                        <div class="flex-1 -ml-px  text-center text-xs text-gray-400 p-2">
                            Cost Plan has no Sections
                        </div>
                    </div>
                </template>
                <cost-plan-breakdown-materials class="my-2" :materials="materials" :readonly="readonly" v-else
                                               :cost-plan="costPlan"
                                               @editMaterial="(from,to) => editMaterial(from,to,'all','')"></cost-plan-breakdown-materials>
            </template>
            <template v-if="breakdown.budgets">
                <h1>Budgets</h1>
                <div class="mb-2">
                    <template v-if="components.length || nonComponents.length">
                        <div
                            v-if="components.length"
                            class="flex mt-2 border-t border-gray-200 bg-gray-50 text-xs text-gray-500 font-semibold">
                            <div class="flex-1 border-r border-l border-gray-200 px-2 py-1 -ml-px">Component</div>
                            <div class="w-28 border-r border-gray-200 px-2 py-1 -ml-px text-right">Materials</div>
                            <div class="w-28 border-r border-gray-200 px-2 py-1 -ml-px text-right">Labour</div>
                            <div class="w-28 border-r border-gray-200 px-2 py-1 -ml-px text-right">Fixed Costs</div>
                        </div>
                        <cost-plan-component-item
                            v-for="(component,index) in components" :item="component" :readOnly="readonly"
                            @change="editComponent"
                            :key="'comp'+index">
                        </cost-plan-component-item>
                        <div
                            v-if="nonComponents.length"
                            class="flex mt-2 border-t border-gray-200 bg-gray-50 text-xs text-gray-500 font-semibold">
                            <div class="flex-1 border-r border-l border-gray-200 px-2 py-1 -ml-px">Non Component Items
                            </div>
                            <div class="w-28 border-r border-gray-200 px-2 py-1 -ml-px text-right">Materials</div>
                            <div class="w-28 border-r border-gray-200 px-2 py-1 -ml-px text-right">Labour</div>
                            <div class="w-28 border-r border-gray-200 px-2 py-1 -ml-px text-right">Fixed Costs</div>
                        </div>
                        <cost-plan-component-item
                            v-for="(item,index) in nonComponents" :item="item" :readOnly="true"
                            :key="'non'+index">
                        </cost-plan-component-item>
                    </template>
                    <div v-else
                         class="flex border bg-gray-50 border-gray-200 mt-1 rounded rounded-lg -mb-px border-gray-200">
                        <div class="flex-1 -ml-px text-center text-xs text-gray-400 p-2">
                            No Components or Non-component Items for budgeting
                        </div>
                    </div>
                    <div
                        class="flex mt-2 border-t border-b border-gray-200 text-gray-700 text-sm font-semibold bg-gray-100 font-semibold">
                        <div class="flex-1 border-r border-l border-gray-200 px-2 py-1 -ml-px">Grand Total</div>
                        <div class="w-28 border-r border-gray-200 px-2 py-1 -ml-px text-right"
                             v-text="toCurrency(mTotal)"></div>
                        <div class="w-28 border-r border-gray-200 px-2 py-1 -ml-px text-right"
                             v-text="toCurrency(lTotal)"></div>
                        <div class="w-28 border-r border-gray-200 px-2 py-1 -ml-px text-right"
                             v-text="toCurrency(fTotal)"></div>
                    </div>
                </div>
            </template>
        </div>
    </ui-sidepage>
</template>

<script>

import {CostPlan} from '@models';
import UiToggle from "../../../ui/UiToggle.vue";
import CostPlanMaterialItem from "./CostPlanMaterialItem.vue";
import UiSidepage from "../../../ui/UiSidepage.vue";
import {resolveNumericText, toCurrency} from "../../../../utils";
import CostPlanBreakdownMaterials from "./CostPlanBreakdownMaterials.vue";
import CostPlanComponentItem from "./CostPlanComponentItem.vue";

export default {
    components: {
        CostPlanComponentItem,
        CostPlanBreakdownMaterials,
        UiSidepage,
        CostPlanMaterialItem,
        UiToggle,
    },
    props: {
        costPlan: Object,
        readonly: true,
        close: Function,
    },
    watch: {
        costPlan: {
            handler(newValue, oldValue) {
                this.renderKey++
            },
            deep: true,
        },
    },
    data() {
        return {
            renderKey: 1,
            materials: [],
            sectionMaterials: {},
            taskMaterials: {},
            itemMaterials: {},
            components: [],
            nonComponents: [],
            split: {
                sections: false,
            },
            breakdown: {
                materials: true,
                budgets: true,
            },
            mTotal: 0,
            lTotal: 0,
            fTotal: 0,
        }
    },
    async mounted() {
        this.setFromItem()
    },
    methods: {
        toCurrency,
        editMaterial(from, to, sourceType, sourceId) {
            this.costPlan.sections.forEach((s) => {
                s.tasks.forEach((t) => {
                    t.items.forEach((i) => {
                        if (i.buildup && i.buildup.materials) {
                            i.buildup.materials.filter((mat) => (mat.name === from.name) && (mat.uom === from.uom)).forEach((mat) => {
                                if (
                                    (sourceType === 'item' && (i.id === sourceId)) ||
                                    (sourceType === 'task' && (t.id === sourceId)) ||
                                    (sourceType === 'section' && (s.id === sourceId)) ||
                                    (sourceType === 'all')) {
                                    mat.uom = to.uom;
                                    mat.name = to.name;
                                }
                            })
                        }
                    })
                })
            })
            this.costPlan.save()
            this.setFromItem()
        },
        editComponent(from, to) {
            this.costPlan.sections.forEach((s) => {
                s.tasks.forEach((t) => {
                    t.items.forEach((i) => {
                        if (i.buildup) {
                            i.buildup.components.filter((c) => c.name === from).forEach((c) => {
                                c.name = to
                            })
                        }
                    })
                })
            })
            this.costPlan.save()
            this.setFromItem()
        },
        setFromItem() {
            let materials = []
            this.sectionMaterials = {}
            this.taskMaterials = {}
            this.itemMaterials = {}

            this.costPlan.sections.filter((i) => !i.omitted).forEach((s) => {
                let sectionMaterials = [];
                s.tasks.filter((i) => !i.omitted).forEach((t) => {
                    let taskMaterials = [];
                    t.items.filter((i) => !i.omitted).forEach((i) => {
                        let itemMaterials = [];
                        if (i.buildup && i.buildup.materials) {
                            i.buildup.materials.forEach((m) => {
                                let material = materials.find((mat) => (mat.name === m.name) && (mat.uom === m.uom));
                                if (material) {
                                    material.quantity += resolveNumericText(m.quantity);
                                } else {
                                    materials.push({
                                        name: m.name,
                                        uom: m.uom,
                                        quantity: resolveNumericText(m.quantity)
                                    });
                                }
                                let sectionMaterial = sectionMaterials.find((mat) => (mat.name === m.name) && (mat.uom === m.uom));
                                if (sectionMaterial) {
                                    sectionMaterial.quantity += resolveNumericText(m.quantity);
                                } else {
                                    sectionMaterials.push({
                                        name: m.name,
                                        uom: m.uom,
                                        quantity: resolveNumericText(m.quantity)
                                    });
                                }
                                let taskMaterial = taskMaterials.find((mat) => (mat.name === m.name) && (mat.uom === m.uom));
                                if (taskMaterial) {
                                    taskMaterial.quantity += resolveNumericText(m.quantity);
                                } else {
                                    taskMaterials.push({
                                        name: m.name,
                                        uom: m.uom,
                                        quantity: resolveNumericText(m.quantity)
                                    });
                                }
                                let itemMaterial = itemMaterials.find((mat) => (mat.name === m.name) && (mat.uom === m.uom));
                                if (itemMaterial) {
                                    itemMaterial.quantity += resolveNumericText(m.quantity);
                                } else {
                                    itemMaterials.push({
                                        name: m.name,
                                        uom: m.uom,
                                        quantity: resolveNumericText(m.quantity)
                                    });
                                }
                            })
                        }
                        itemMaterials.sort((a, b) => a.name.localeCompare(b.name));
                        this.itemMaterials[i.id] = itemMaterials;
                    })
                    taskMaterials.sort((a, b) => a.name.localeCompare(b.name));
                    this.taskMaterials[t.id] = taskMaterials;
                })
                sectionMaterials.sort((a, b) => a.name.localeCompare(b.name));
                this.sectionMaterials[s.id] = sectionMaterials;
            })
            materials.sort((a, b) => a.name.localeCompare(b.name));
            this.materials = materials;
            let components = []
            let nonComponents = []
            let mTotal = 0
            let fTotal = 0
            let lTotal = 0
            this.costPlan.sections.filter((i) => !i.omitted).forEach((s) => {
                s.tasks.filter((i) => !i.omitted).forEach((t) => {
                    t.items.filter((i) => !i.omitted).forEach((i) => {
                        if (i.buildup) {
                            i.buildup.components.forEach((c) => {
                                let component = components.find((com) => com.name === c.name);
                                let material = i.buildup.materials.filter((m) => (m.component) && (m.component.name === c.name)).reduce((a, m) => a + resolveNumericText(m.value), 0)
                                let labour = i.buildup.labour.filter((m) => (m.component) && (m.component.name === c.name)).reduce((a, m) => a + resolveNumericText(m.value), 0)
                                let fixedCost = i.buildup.fixedCosts.filter((m) => (m.component) && (m.component.name === c.name)).reduce((a, m) => a + resolveNumericText(m.value), 0)
                                if (component) {
                                    component.materials += material;
                                    component.labour += labour;
                                    component.fixedCost += fixedCost;
                                } else {
                                    components.push({
                                        name: c.name,
                                        materials: material,
                                        labour: labour,
                                        fixedCosts: fixedCost
                                    });
                                }
                                mTotal += material
                                lTotal += labour
                                fTotal += fixedCost
                            })
                            let material = i.buildup.materials.filter((m) => (!m.component)).reduce((a, m) => a + resolveNumericText(m.value), 0)
                            let labour = i.buildup.labour.filter((m) => (!m.component)).reduce((a, m) => a + resolveNumericText(m.value), 0)
                            let fixedCost = i.buildup.fixedCosts.filter((m) => (!m.component)).reduce((a, m) => a + resolveNumericText(m.value), 0)
                            if (material || labour || fixedCost) {
                                nonComponents.push({
                                    name: (i.reference ? (i.reference + ' - ') : '') + i.description,
                                    materials: material,
                                    labour: labour,
                                    fixedCosts: fixedCost
                                })
                                mTotal += material
                                lTotal += labour
                                fTotal += fixedCost
                            }
                        }
                    })
                })
            })
            this.mTotal = mTotal
            this.lTotal = lTotal
            this.fTotal = fTotal
            nonComponents.sort((a, b) => a.name.localeCompare(b.name));
            this.nonComponents = nonComponents
            components.sort((a, b) => a.name.localeCompare(b.name)).forEach((a) => a.name = a.name.trim()?a.name.trim():' ')
            this.components = components
        },
    }
}
</script>
